/**
 * UniversalTextEditor
 * ───────────────────
 * Lets the admin edit ANY text on any page without wrapping individual
 * strings in <EditableText>. Two modes:
 *
 *  1. VIEW (always on, for everyone): fetch `/api/text-overrides?path=...`
 *     for the current URL on every route change, then walk the DOM and
 *     replace text content of matching nodes.
 *
 *  2. EDIT (admin + inline-edit toggle on): walk the DOM, find every
 *     text-bearing element, add `contenteditable` + a red outline + a
 *     small Save/Reset toolbar that POSTs the change to
 *     `/api/admin/text-overrides` on save.
 *
 * Identification:
 *   key = sha1(`${tag}|${normalizedOriginalText.slice(0,80)}`) — stable
 *   across page reloads as long as the tag and original text remain the
 *   same. Layout reorders / class changes do NOT break it.
 *
 * Coexists with the existing EditableText system — we skip any element
 * that is already inside a `[data-testid^="editable-"]` wrapper.
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useInlineEdit } from '@/contexts/InlineEditContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EDITABLE_TAGS = new Set([
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'P', 'SPAN', 'LI', 'A', 'BUTTON',
  'STRONG', 'EM', 'BLOCKQUOTE', 'TD', 'TH',
  'LABEL', 'SUMMARY', 'FIGCAPTION', 'CAPTION',
]);

const SKIP_SELECTORS = [
  // existing CMS-aware text already has its own editor
  '[data-testid^="editable-"]',
  // skip the admin chrome itself
  '[data-cms-skip]',
  // form fields
  'input', 'textarea', 'select', 'option',
  // scripts/styles/svg internals
  'script', 'style', 'svg',
  // ChatWidget, toggles, cookie banner — leave them alone
  '#emergent-badge',
  '[data-testid="inline-edit-toggle"]',
  '[data-testid="inline-edit-badge"]',
];

const normalize = (s) => (s || '').replace(/\s+/g, ' ').trim();

// Lightweight non-crypto hash — collision-safe at this scale (a few k strings)
const hashKey = (tag, text) => {
  const input = `${tag}|${normalize(text).slice(0, 80)}`;
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0;
  }
  // Suffix length so two strings with the same hash but different lengths
  // are still distinguishable.
  return `t${h.toString(36)}_${normalize(text).length}`;
};

const isEditableElement = (el) => {
  if (!el || el.nodeType !== 1) return false;
  if (!EDITABLE_TAGS.has(el.tagName)) return false;
  // Must contain a non-empty text node directly inside (not just nested elements)
  let hasDirectText = false;
  for (const c of el.childNodes) {
    if (c.nodeType === Node.TEXT_NODE && normalize(c.textContent).length >= 2) {
      hasDirectText = true;
      break;
    }
  }
  if (!hasDirectText) return false;
  // Skip if inside any blocked container
  for (const sel of SKIP_SELECTORS) {
    try { if (el.closest(sel)) return false; } catch (e) { /* invalid selector */ }
  }
  return true;
};

const walkEditableElements = (root, fn) => {
  const stack = [root];
  while (stack.length) {
    const node = stack.pop();
    if (!node || node.nodeType !== 1) continue;
    if (isEditableElement(node)) fn(node);
    for (const child of node.children) stack.push(child);
  }
};

const UniversalTextEditor = () => {
  const { pathname } = useLocation();
  const { editMode, isAdmin } = useInlineEdit();
  const overridesRef = useRef({});
  const appliedAtRef = useRef(0);
  const cleanupRef = useRef(() => {});

  // Disable everything on admin routes — those pages have their own Save
  // buttons (AdminPageEditor, AdminBlog, AdminSEO, etc.) which were getting
  // armed as contenteditable, preventing clicks. The universal editor is
  // for the PUBLIC site only.
  const isAdminRoute = pathname.startsWith('/admin');

  // ── 1. Fetch overrides & apply them on every route change ────────────────
  useEffect(() => {
    if (isAdminRoute) return; // never run on admin routes
    let cancelled = false;
    const apply = async () => {
      try {
        const { data } = await axios.get(`${API}/text-overrides`, { params: { path: pathname } });
        if (cancelled) return;
        const map = {};
        (data.items || []).forEach((it) => { map[it.key] = it.content; });
        overridesRef.current = map;
        // Walk the DOM in a few passes so we catch lazy-loaded sections too.
        const doApply = () => {
          walkEditableElements(document.body, (el) => {
            // Find the first text node to read the original.
            const textNode = Array.from(el.childNodes).find(
              (n) => n.nodeType === Node.TEXT_NODE && normalize(n.textContent).length >= 2
            );
            if (!textNode) return;
            const original = textNode.textContent;
            const key = hashKey(el.tagName, original);
            el.setAttribute('data-cms-key', key);
            el.setAttribute('data-cms-original', normalize(original).slice(0, 200));
            if (map[key] !== undefined && map[key] !== original) {
              // Only replace if the visible text still matches the captured
              // original or a previous override of ours — never overwrite
              // a fresh runtime value (e.g. live counters).
              const flagged = el.getAttribute('data-cms-applied');
              if (!flagged || flagged === 'true') {
                textNode.textContent = map[key];
                el.setAttribute('data-cms-applied', 'true');
              }
            }
          });
          appliedAtRef.current = Date.now();
        };
        // Initial + a few re-applies as React hydrates / lazy chunks mount.
        doApply();
        const t1 = setTimeout(doApply, 250);
        const t2 = setTimeout(doApply, 1000);
        const t3 = setTimeout(doApply, 2500);
        cleanupRef.current = () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
      } catch (e) {
        // Silent — overrides are non-critical.
      }
    };
    apply();
    return () => { cancelled = true; cleanupRef.current(); };
  }, [pathname, isAdminRoute]);

  // ── 2. Edit mode: contenteditable + click-to-save ────────────────────────
  const armEditMode = useCallback(() => {
    if (!isAdmin || !editMode || isAdminRoute) return () => {};
    const touched = [];
    const wireOne = (el) => {
      if (el.hasAttribute('data-cms-armed')) return;
      el.setAttribute('data-cms-armed', '1');
      el.setAttribute('contenteditable', 'true');
      el.setAttribute('spellcheck', 'false');
      el.classList.add('cms-editable-outline');
      const onFocus = () => {
        el.dataset.cmsBefore = el.textContent;
      };
      const onBlur = async () => {
        const before = el.dataset.cmsBefore || '';
        const after = el.textContent;
        if (normalize(before) === normalize(after)) return;
        const key = el.getAttribute('data-cms-key');
        const original = el.getAttribute('data-cms-original') || before;
        try {
          await axios.post(`${API}/admin/text-overrides`, {
            path: pathname,
            key,
            original,
            content: after,
          });
          overridesRef.current[key] = after;
          el.setAttribute('data-cms-applied', 'true');
          toast.success('Saved');
        } catch (err) {
          el.textContent = before;
          toast.error('Save failed — are you signed in as admin?');
        }
      };
      const onKey = (e) => {
        if (e.key === 'Escape') { el.textContent = el.dataset.cmsBefore || el.textContent; el.blur(); }
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); el.blur(); }
      };
      const onClick = (e) => {
        // Prevent navigation when editing links/buttons
        if (el.tagName === 'A' || el.tagName === 'BUTTON') {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      el.addEventListener('focus', onFocus);
      el.addEventListener('blur', onBlur);
      el.addEventListener('keydown', onKey);
      el.addEventListener('click', onClick, true);
      touched.push({ el, onFocus, onBlur, onKey, onClick });
    };
    walkEditableElements(document.body, wireOne);
    // Also wire any newly added elements (lazy sections).
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) {
            if (isEditableElement(node)) wireOne(node);
            walkEditableElements(node, wireOne);
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      touched.forEach(({ el, onFocus, onBlur, onKey, onClick }) => {
        el.removeEventListener('focus', onFocus);
        el.removeEventListener('blur', onBlur);
        el.removeEventListener('keydown', onKey);
        el.removeEventListener('click', onClick, true);
        el.removeAttribute('contenteditable');
        el.removeAttribute('data-cms-armed');
        el.removeAttribute('spellcheck');
        el.classList.remove('cms-editable-outline');
      });
    };
  }, [isAdmin, editMode, pathname]);

  useEffect(() => {
    const cleanup = armEditMode();
    return cleanup;
  }, [armEditMode, isAdminRoute]);

  return null;
};

export default UniversalTextEditor;
